import { Component, ViewEncapsulation, ViewChild, Output, EventEmitter, Input, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformServer } from '@angular/common';


import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil, defaultIfEmpty, filter } from 'rxjs/operators';
import { ImageCroppedEvent, ImageCropperComponent, CropperPosition, Dimensions } from 'ngx-image-cropper';

import { MediaFile, ImageFormat, ImageCropping, dataUrlToFile, imageUrlToDataUrl } from '../../models';


@Component({
    selector: 'upload-cropper',
    templateUrl: './upload-cropper.component.html',
    styleUrls: ['./upload-cropper.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class UploadCropperComponent {
    @Output() croppingModeEnabledChanged = new EventEmitter<boolean>();
    @Output() croppingError = new EventEmitter<string>();

    @Input() set croppingModeEnabled(enable: boolean) {
        this.showCropper = enable;
    };

    public cropFileBase64: string;
    public showCropper: boolean = false;
    public errorStr: string = "";
    public canvasRotation: number = 0;
    private cancelCropping = new Subject<void>();
    public isRunningSsr: boolean = false;
    public imageFormat: ImageFormat = null;
    private isManualSaveRequested: boolean = false;

    @ViewChild('imageCropper') imageCropper: ImageCropperComponent;

    constructor(@Inject(PLATFORM_ID) platformId: Object) {
        this.isRunningSsr = isPlatformServer(platformId);
    }

    public async cropImage(cropFile: MediaFile, originFile: MediaFile, imageFormat: ImageFormat, forceShowCropper: boolean = false, roundCropper: boolean = false): Promise<[File, string, CropperPosition, number]> {
        // Reload last saved rotation
        if(cropFile.rotation != null) this.canvasRotation = cropFile.rotation;

        if(!imageFormat) {
            console.warn("Could not crop image, no cropping format for " + cropFile.type);
            return [cropFile.blob, cropFile.url, null, cropFile.rotation];
        }

        if(!cropFile) {
            console.warn("Skip cropping no crop file given..? Origin:", originFile);
            return null;
        }
        // Load original image as blob if the blob does not already exist
        if(!originFile.blob) {
            let dataUrl = await imageUrlToDataUrl(originFile.url);
            originFile.blob = dataUrlToFile(dataUrl, 'origin.png');
        }

        this.cropFileBase64 = await this.convertFileToBase64(originFile.blob);
        // This combination of timeout and indicating to the cropper that is is loaded in view ensures that the ready signal is fired correctly
        // so even though it seems stupid, this is the only way the image cropper will fire more than one ready event
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 200));
        this.imageCropper.imageLoadedInView();

        let autoCrop = imageFormat.autoCrop && !forceShowCropper;

        // Make sure the image format is actually applied (it seems it is not enough to apply programmatically anymore)
        this.imageFormat = imageFormat;

        // Obs only one crop image operation must be called at a time to avoid async issue (TODO: maybe protect with mutex?)
        // Load image in cropper and specify image format
        this.imageCropper.maintainAspectRatio = imageFormat.maintainAspectRatio;
        this.imageCropper.aspectRatio = imageFormat.aspectRatio;
        this.imageCropper.resizeToWidth = imageFormat.resizeToWidth;
        this.imageCropper.format = imageFormat.format;
        this.imageCropper.roundCropper = roundCropper;

        // Never rely on ngx-cropper auto cropping feature (its unpredictable)
        this.imageCropper.autoCrop = false;
        // this.imageCropper.roundCropper = true;
        this.imageCropper.onlyScaleDown = true;

        // Enable cropping mode if not auto cropping is specified or view forced (by manual cropping button press)
        this.enableCroppingMode(!autoCrop);

        // Wait for cropper to load before attempting to set old cropper position
        this.imageCropper.hidden = false;

        // Wait for cropper to load image when auto cropping (a new image was uploaded)
        // or on manual cropping first time - the second time the same image is loaded the cropper will not fire ready signal again
        if(autoCrop || !this.imageCropper.imageVisible) {
            const dimensions = await this.awaitCropperReadyOrCancel();
            // Check if cropping was canceled
            if(!dimensions) {
                this.enableCroppingMode(false);
                return null;
            }
        }

        // Reload last saved cropping area
        if(cropFile.cropping) { // && !autoCrop
            let element: HTMLImageElement = this.imageCropper.sourceImage.nativeElement as HTMLImageElement;
            // Get dimensions of image cropper
            let elementWidth = element.offsetWidth;
            let imageWidth = element.naturalWidth;
            let scale = elementWidth / imageWidth;
            // Scale last cropping from image coordinates to cropper coordinates
            let cropping = cropFile.cropping;
            let scaledCropping: ImageCropping = {
                x1: cropping.x1 * scale,
                x2: cropping.x2 * scale,
                y1: cropping.y1 * scale,
                y2: cropping.y2 * scale
            }
            this.imageCropper.cropper = scaledCropping;
        }

        let croppedEv: ImageCroppedEvent;
        // Wait for user/cropper to finish cropping
        if(autoCrop) croppedEv = await this.imageCropper.crop();
        if(!autoCrop) {
            // Some cropper state updates emit imageCropped automatically.
            // In manual mode, only accept events after the user explicitly clicks Save.
            croppedEv = await firstValueFrom(
                this.imageCropper.imageCropped.pipe(
                    takeUntil(this.cancelCropping),
                    filter(() => this.isManualSaveRequested)
                )
            ).catch(cancel => null);
            this.isManualSaveRequested = false;
        }

        // Close cropper
        this.enableCroppingMode(false);

        // If cropping was canceled
        if(!croppedEv) return null;

        // Generate data file from cropped event
        const base64 = await this.convertFileToBase64(croppedEv.blob);
        const file: File = dataUrlToFile(base64, 'origin.png');

        // Make copy of cropping data
        const cropping = JSON.parse(JSON.stringify(croppedEv.imagePosition));
        return [file, base64, cropping, this.canvasRotation];
    }

    public saveCropping() {
        this.isManualSaveRequested = true;
        this.imageCropper.crop();
    }

    public abortCropping() {
        this.isManualSaveRequested = false;
        this.cancelCropping.next();
    }

    private enableCroppingMode(enable: boolean) {
        // Clear any errors
        this.errorStr = "";
        this.showCropper = enable;
        this.croppingModeEnabledChanged.emit(enable);
    }

    public async rotateRight() {
        // Rotate image (0 = 0 deg, 1 = 90 deg ...)
        let currentCrop = { ...this.imageCropper.cropper };
        this.canvasRotation = (this.canvasRotation + 1) % 4;
        // Wait for image to rotate
        await this.awaitCropperReadyOrCancel();
        // Set cropper as before rotation
        this.restoreCropper(currentCrop);
    }

    private restoreCropper(cropper: ImageCropping) {
        let imageWidth = this.imageCropper.maxSize.width;
        let imageHeight = this.imageCropper.maxSize.height;
        let cropperWidth = cropper.x2 - cropper.x1;
        let cropperHeight = cropper.y2 - cropper.y1;

        // If cropper extends image => convert to default
        if(cropperWidth > imageWidth || cropperHeight > imageHeight) {
            this.imageCropper.resetCropperPosition();
            return;
        }

        // Move cropper to image center
        cropper.x1 = imageWidth / 2 - cropperWidth / 2;
        cropper.x2 = imageWidth / 2 + cropperWidth / 2;
        cropper.y1 = imageHeight / 2 - cropperHeight / 2;
        cropper.y2 = imageHeight / 2 + cropperHeight / 2;

        this.imageCropper.cropper = cropper;
    }

    /**
     * Temporary show an error to the user and then hide it again
     */
    public showError(err: string) {
        console.warn(err);
        this.errorStr = err;
        this.croppingError.emit(err);
    }

    // Convert a File to a base64 string
    private convertFileToBase64(file: File | Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    }

    private awaitCropperReadyOrCancel() : Promise<Dimensions> {
        return firstValueFrom(this.imageCropper.cropperReady.pipe(takeUntil(this.cancelCropping), defaultIfEmpty(null)));
    }
}
